import React, { useEffect, useState } from "react";
import { useProfile } from "../context/ProfileContext";
import { TIERS, tierFor } from "../admin/adminUi";

// Tier-specific look for the badge pill.
const TIER_BADGE = {
  BRONZE: "bg-[#7A3B1B] text-white",
  SILVER: "bg-[#2B1B17] text-white",
  GOLD: "bg-gradient-to-r from-[#E65C00] to-[#F9D423] text-[#2B1B17]",
  UNRANKED: "bg-black/10 text-black/50",
};

const ProfileCard = ({ showReferral = false }) => {
  const { data, refresh } = useProfile();

  // If we arrived without data (e.g. right after login), load it once.
  useEffect(() => {
    if (!data) refresh();
  }, [data, refresh]);

  const profile = data?.profile || {};
  const totalPoints = data?.totalPoints || 0;
  const totalAvailable = data?.totalAvailablePoints || 0;
  const referralCode = data?.referralCode || "";

  const [copied, setCopied] = useState(false);
  const copyCode = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  // Progress is measured against the FULL pool (total points available across
  // all live tasks) so the denominator is identical for every CA, new or old,
  // regardless of their current tier or join date. The tier is still a SHARE of
  // that pool (GOLD >= 80%, SILVER >= 50%), shown as the badge + next-tier hint.
  const hasPool = totalAvailable > 0;
  const tierName = tierFor(totalPoints, totalAvailable);
  const tierIdx = TIERS.findIndex((t) => t.name === tierName);
  const nextTier = TIERS[tierIdx + 1] || null; // null once at the top tier (GOLD)

  // Referral points add to a CA's total but NOT to the task pool, so raw points
  // can exceed the pool. Clamp the displayed share to 100% (a maxed pool).
  const poolPercent = hasPool
    ? Math.min(100, Math.round((totalPoints / totalAvailable) * 100))
    : 0;
  const poolProgressPercent = hasPool
    ? Math.min(100, (totalPoints / totalAvailable) * 100)
    : 0;
  const nextTierPts = nextTier ? Math.ceil(nextTier.pct * totalAvailable) : 0;
  const ptsToGo = nextTier ? Math.max(0, nextTierPts - totalPoints) : 0;

  return (
    <div className="w-full lg:w-[380px] bg-white/20 backdrop-blur-xl border border-black/10 rounded-2xl p-6 shadow-2xl text-[#2B1B17] font-['Montserrat',sans-serif]">
      <div className="flex gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-black/10 flex-shrink-0 bg-[#D15B28] flex items-center justify-center font-bold text-xl text-white">
          {profile.name?.charAt(0).toUpperCase() || "U"}
        </div>
        <div>
          <h3 className="text-lg font-bold leading-tight">{profile.name || "User"}</h3>
          <p className="text-xs text-black/50 mt-0.5">
            {profile.college || "—"}
          </p>
          {profile.memberSince && (
            <p className="text-xs text-black/50">
              Joined {new Date(profile.memberSince).toLocaleString("default", { month: "short", year: "numeric" })}
            </p>
          )}
          <div className={`inline-flex items-center gap-1 ${TIER_BADGE[tierName]} text-[10px] font-black tracking-wider px-2.5 py-1 rounded-full mt-2.5 uppercase`}>
            <span>✦</span> {tierName === "UNRANKED" ? "Unranked" : `${tierName} CA`}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-[11px] font-bold tracking-wide text-black/80 mb-1.5">
          <span>
            {!hasPool ? "TIER PROGRESS" : "TOTAL PROGRESS"}
          </span>
          <span>
            {totalPoints.toLocaleString()}
            {hasPool ? ` / ${totalAvailable.toLocaleString()}` : ""} pts
          </span>
        </div>
        <div className="w-full h-1.5 bg-black/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#E65C00] to-[#F9D423] rounded-full"
            style={{ width: `${poolProgressPercent}%` }}
          />
        </div>
        <p className="text-[11px] text-black/50 mt-1.5 font-medium">
          {!hasPool
            ? "Tiers unlock as tasks go live"
            : nextTier
            ? `${poolPercent}% of pool · ${ptsToGo.toLocaleString()} pts to ${nextTier.name} CA`
            : `Top tier · ${poolPercent}% of pool 🏆`}
        </p>
      </div>

      {showReferral && referralCode && (
        <div className="mt-4 pt-4 border-t border-black/10">
          <div className="text-[11px] font-bold tracking-wide text-black/80 mb-1.5">
            YOUR REFERRAL CODE
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 px-2 sm:px-3 py-2 rounded-lg bg-[white]/20 text-black text-sm sm:text-base font-extrabold tracking-wider sm:tracking-widest text-center break-all">
              {referralCode}
            </code>
            <button
              onClick={copyCode}
              className="flex-shrink-0 px-3 sm:px-4 py-2 rounded-lg bg-[white]/10 text-[#D15B28] text-sm font-bold hover:bg-[#D15B28]/20 transition whitespace-nowrap"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p className="text-[11px] text-black/50 mt-1.5 font-medium">
            Share this code — you earn points when someone registers with it.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfileCard;